import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import logo from './assets/logo.png';

const VEHICLES_API = `${process.env.REACT_APP_API_URL}/api/vehicles`;
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800';

const isSafeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    return u.protocol === 'https:';
  } catch {
    return false;
  }
};

const getSafeImage = (url) => (isSafeImageUrl(url) ? url : FALLBACK_IMAGE);

const sanitize = (value) => {
  if (typeof value !== 'string') return '';
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const formatPrice = (price) => {
  if (!price || price === 'Contact for pricing') return price;
  const num = parseFloat(price);
  if (isNaN(num) || num < 0 || num > 999999999) return 'Contact for pricing';
  return `Nu. ${num.toLocaleString('en-US', { maximumFractionDigits: 0 })} + GST`;
};

function HomeScreen() {
  const [scrolled, setScrolled] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [displayedVehicles, setDisplayedVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [hoveredLink, setHoveredLink] = useState(null);

  const viewLinks = useMemo(() => [
    { to: '/vehicles', label: 'Showroom', },
    { to: '/lease-to-own-viewer', label: 'Lease to Own', },
    { to: '/client-hiring', label: 'Hire',  },
    { to: '/airport-service', label: 'Airport Service',  },
    { to: '/company-background', label: 'About',},
    { to: '/locations', label: 'Locations',}
  ], []);

  // Scroll + resize
  useEffect(() => {
    let scrollTimer;
    const onScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => setScrolled(window.scrollY > 50), 16);
    };

    const onResize = () => setIsMobile(window.innerWidth <= 768);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    return () => {
      clearTimeout(scrollTimer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Fetch vehicles — secured
  useEffect(() => {
    let mounted = true;
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 10000);

    const load = async () => {
      if (!VEHICLES_API) {
        setError('Configuration error');
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(VEHICLES_API, {
          signal: ctrl.signal,
          headers: {
            Accept: 'application/json',
            'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
          },
          credentials: 'same-origin',
        });
        clearTimeout(timeout);

        if (!res.ok) throw new Error('Network response was not ok');

        if (!res.headers.get('content-type')?.includes('application/json')) {
          throw new Error('Invalid response type');
        }

        const json = await res.json();

        if (!mounted) return;

        if (json?.success && Array.isArray(json.data)) {
          const safe = json.data
            .filter(Boolean)
            .map(v => ({
              id: sanitize(String(v._id || '')),
              name: sanitize(v.name || 'Luxury Vehicle'),
              price: sanitize(String(v.price || 'Contact for pricing')),
              image: getSafeImage(v.imageURL)
            }));

          const shuffled = [...safe].sort(() => 0.5 - Math.random());
          setDisplayedVehicles(shuffled.slice(0, Math.min(9, shuffled.length)));
        } else {
          setDisplayedVehicles([]);
        }

        setError(null);
      } catch (e) {
        if (!mounted) return;
        if (e.name === 'AbortError') {
          setError('Request timed out');
        } else {
          console.error(e);
          setError('Could not load vehicles');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
      clearTimeout(timeout);
      ctrl.abort();
    };
  }, []);

  const handleExternalLink = useCallback((url) => {
    try {
      const urlObj = new URL(url);
      if (urlObj.protocol !== 'https:') {
        console.error('Only HTTPS links are allowed');
        return '#';
      }
      return url;
    } catch {
      console.error('Invalid URL');
      return '#';
    }
  }, []);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0F1419',
        color: '#E8E8E8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '1.5rem',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100vw'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '3px solid rgba(199, 179, 129, 0.2)',
          borderTop: '3px solid #C7B381',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} aria-label="Loading"></div>
        <div style={{ fontSize: '1.25rem', fontWeight: '300', letterSpacing: '0.1em' }}>
          Loading luxury collection...
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error && displayedVehicles.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0F1419',
        color: '#E8E8E8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '1.5rem',
        padding: '2rem',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100vw'
      }}>
        <div style={{ fontSize: '3rem' }}>⚠️</div>
        <div style={{ fontSize: '1.25rem', fontWeight: '300', textAlign: 'center' }}>
          {error}
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: 'linear-gradient(135deg, #C7B381 0%, #A89968 100%)',
            color: '#0F1419',
            padding: '1rem 2rem',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const hero = displayedVehicles[0];
  const others = displayedVehicles.slice(1, 5);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom, #0F1419, #1A1F2E)',
      color: '#E8E8E8',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      overflow: 'hidden',
      width: '100%',
      maxWidth: '100vw',
      position: 'relative'
    }}>
      {/* Premium Navigation */}
      <nav
        role="navigation"
        aria-label="Main navigation"
        style={{
          position: 'fixed',
          width: '100%',
          maxWidth: '100vw',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          background: scrolled 
            ? 'linear-gradient(to bottom, rgba(15, 20, 25, 0.98), rgba(15, 20, 25, 0.95))' 
            : 'linear-gradient(to bottom, rgba(15, 20, 25, 0.8), transparent)',
          backdropFilter: scrolled ? 'blur(20px)' : 'blur(10px)',
          boxShadow: scrolled ? '0 4px 30px rgba(0, 0, 0, 0.3)' : 'none'
        }}
      >
        <div style={{
          maxWidth: '1600px',
          margin: '0 auto',
          padding: 'clamp(1.2rem, 2.5vw, 1.8rem) clamp(2rem, 5vw, 4rem)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Logo Section */}
          <Link
            to="/"
            aria-label="ST Motors Home"
            style={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(0.6rem, 1.5vw, 0.9rem)',
              zIndex: 10,
              transition: 'transform 0.3s ease'
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            <div style={{
              width: 'clamp(45px, 7vw, 60px)',
              height: 'clamp(45px, 7vw, 60px)',
              background: 'linear-gradient(135deg, rgba(199, 179, 129, 0.15), rgba(199, 179, 129, 0.05))',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(199, 179, 129, 0.2)',
              padding: '0.5rem',
              boxSizing: 'border-box'
            }}>
              <img
                src={logo}
                alt="ST Motors Logo"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'contain'
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
              <div style={{
                fontSize: 'clamp(1.1rem, 2.3vw, 1.4rem)',
                fontWeight: '700',
                color: '#FFFFFF',
                letterSpacing: '0.05em',
                lineHeight: '1'
              }}>
                ST MOTORS
              </div>
              <div style={{
                fontSize: 'clamp(0.6rem, 1.2vw, 0.7rem)',
                fontWeight: '400',
                color: '#C7B381',
                letterSpacing: '0.15em',
                textTransform: 'uppercase'
              }}>
                Electric Luxury
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          {!isMobile ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(0.3rem, 0.8vw, 0.5rem)',
             // background: 'rgba(26, 31, 46, 0.6)',
              padding: '0.6rem',
              borderRadius: '50px',
              //border: '1px solid rgba(199, 179, 129, 0.15)',
             // boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
            }}>
              {viewLinks.map((link, idx) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onMouseEnter={() => setHoveredLink(idx)}
                  onMouseLeave={() => setHoveredLink(null)}
                  style={{
                    position: 'relative',
                    padding: 'clamp(0.6rem, 1.2vw, 0.8rem) clamp(1.2rem, 2.2vw, 1.8rem)',
                    borderRadius: '50px',
                    color: hoveredLink === idx ? '#0F1419' : '#E8E8E8',
                    textDecoration: 'none',
                    fontSize: 'clamp(0.8rem, 1.4vw, 0.9rem)',
                    fontWeight: '600',
                    letterSpacing: '0.02em',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: hoveredLink === idx 
                      ? 'linear-gradient(135deg, #C7B381 0%, #E8D4A0 100%)'
                      : 'transparent',
                    transform: hoveredLink === idx ? 'translateY(-2px)' : 'translateY(0)',
                    boxShadow: hoveredLink === idx 
                      ? '0 8px 20px rgba(199, 179, 129, 0.3)'
                      : 'none'
                  }}
                >
                  <span style={{ fontSize: '1rem' }}>{link.icon}</span>
                  {link.label}
                </Link>
              ))}
            </div>
          ) : (
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              style={{
                background: 'linear-gradient(135deg, rgba(199, 179, 129, 0.2), rgba(199, 179, 129, 0.1))',
                border: '1px solid rgba(199, 179, 129, 0.3)',
                borderRadius: '12px',
                padding: '0.9rem 1.3rem',
                color: '#C7B381',
                cursor: 'pointer',
                fontSize: '1.2rem',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {mobileMenuOpen ? '✕' : '☰'}
            </button>
          )}
        </div>

        {/* Mobile Menu */}
        {isMobile && (
          <div style={{
            maxHeight: mobileMenuOpen ? '600px' : '0',
            overflow: 'hidden',
            transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            background: 'linear-gradient(to bottom, rgba(15, 20, 25, 0.98), rgba(26, 31, 46, 0.98))',
            backdropFilter: 'blur(20px)',
            borderTop: mobileMenuOpen ? '1px solid rgba(199, 179, 129, 0.15)' : 'none',
            width: '100%',
            maxWidth: '100vw',
            boxSizing: 'border-box'
          }}>
            <div style={{ 
              padding: '2rem 1.5rem', 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '0.8rem',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              {viewLinks.map((link, idx) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    padding: '1.2rem 1.5rem',
                    background: 'linear-gradient(135deg, rgba(199, 179, 129, 0.08), rgba(199, 179, 129, 0.03))',
                    border: '1px solid rgba(199, 179, 129, 0.2)',
                    borderRadius: '12px',
                    color: '#E8E8E8',
                    textDecoration: 'none',
                    fontSize: '1rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(199, 179, 129, 0.15), rgba(199, 179, 129, 0.08))';
                    e.currentTarget.style.borderColor = 'rgba(199, 179, 129, 0.4)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(199, 179, 129, 0.08), rgba(199, 179, 129, 0.03))';
                    e.currentTarget.style.borderColor = 'rgba(199, 179, 129, 0.2)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <span style={{ fontSize: '1.3rem' }}>{link.icon}</span>
                  <span>{link.label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - with logo background */}
      <section style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, rgba(15, 20, 25, 0.98) 0%, rgba(26, 31, 46, 0.95) 100%)',
        paddingTop: '80px',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box'
      }}>
        {/* Logo Background Watermark */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.03,
          pointerEvents: 'none',
          overflow: 'hidden'
        }}>
          <img
            src={logo}
            alt=""
            style={{
              width: isMobile ? '150%' : '80%',
              height: 'auto',
              objectFit: 'contain',
              filter: 'grayscale(100%)'
            }}
          />
        </div>

        {/* Gradient Overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 30% 50%, rgba(199, 179, 129, 0.08) 0%, transparent 60%)',
          pointerEvents: 'none'
        }}></div>

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: 'clamp(8rem, 15vw, 10rem) clamp(2rem, 5vw, 4rem) clamp(2rem, 4vw, 4rem)',
          position: 'relative',
          zIndex: 1,
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{ maxWidth: isMobile ? '100%' : '800px' }}>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 8vw, 5.5rem)',
              fontWeight: '300',
              lineHeight: '1.1',
              marginBottom: 'clamp(1.5rem, 3vw, 2rem)',
              letterSpacing: '-0.02em',
              color: '#FFFFFF'
            }}>
              Experience
              <br />
              <span style={{
                fontWeight: '600',
                background: 'linear-gradient(135deg, #C7B381 0%, #E8D4A0 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Unparalleled
              </span>
              <br />
              Luxury
            </h1>

            <p style={{
              fontSize: 'clamp(1rem, 2vw, 1.15rem)',
              lineHeight: '1.8',
              color: '#B8B8B8',
              marginBottom: 'clamp(2rem, 4vw, 3rem)',
              maxWidth: isMobile ? '100%' : '600px',
              fontWeight: '300'
            }}>
              Curated collection of the world's most prestigious electric vehicles.
              Each journey crafted to perfection, every detail considered.
            </p>

            <div style={{ 
              display: 'flex', 
              gap: 'clamp(1rem, 2vw, 1.5rem)', 
              alignItems: 'center', 
              flexWrap: 'wrap' 
            }}>
              <Link
                to="/vehicles"
                style={{
                  background: 'linear-gradient(135deg, #C7B381 0%, #A89968 100%)',
                  color: '#0F1419',
                  padding: 'clamp(1rem, 2vw, 1.25rem) clamp(2rem, 4vw, 3rem)',
                  border: 'none',
                  borderRadius: '50px',
                  fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  boxShadow: '0 8px 32px rgba(199, 179, 129, 0.3)',
                  transition: 'all 0.3s ease',
                  display: 'inline-block'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(199, 179, 129, 0.4)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(199, 179, 129, 0.3)';
                }}
              >
                Explore Collection
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Spotlight section */}
      <section id="collection" style={{
        padding: 'clamp(4rem, 10vw, 8rem) 0',
        background: '#0F1419',
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box'
      }}>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '1200px',
          height: '1200px',
          background: 'radial-gradient(circle, rgba(199, 179, 129, 0.15) 0%, transparent 60%)',
          pointerEvents: 'none',
          animation: 'pulse 8s ease-in-out infinite'
        }}></div>

        <div style={{ 
          maxWidth: '1600px', 
          margin: '0 auto', 
          padding: '0 clamp(2rem, 5vw, 4rem)', 
          position: 'relative', 
          zIndex: 1,
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: 'clamp(3rem, 6vw, 5rem)',
            maxWidth: '900px',
            margin: '0 auto clamp(4rem, 8vw, 6rem)'
          }}>
            <div style={{ position: 'relative', marginBottom: 'clamp(2rem, 4vw, 3rem)' }}>
              <h2 style={{
                fontSize: 'clamp(3rem, 9vw, 6rem)',
                fontWeight: '200',
                color: '#FFFFFF',
                letterSpacing: '-0.03em',
                lineHeight: '0.9'
              }}>
                The
                <br />
                <span style={{
                  fontWeight: '700',
                  fontSize: 'clamp(3.5rem, 10vw, 7rem)',
                  background: 'linear-gradient(135deg, #C7B381 0%, #FFFFFF 50%, #C7B381 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  backgroundSize: '200% auto',
                  animation: 'shimmer 3s linear infinite'
                }}>
                  Spotlight
                </span>
              </h2>
            </div>
          </div>

          {displayedVehicles.length > 0 ? (
            <>
              {hero && (
                <div style={{ 
                  marginBottom: 'clamp(4rem, 8vw, 6rem)', 
                  position: 'relative',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  <div
                    onMouseEnter={() => setHoveredCard(hero.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    style={{
                      position: 'relative',
                      maxWidth: '1400px',
                      margin: '0 auto',
                      height: isMobile ? '500px' : '650px',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      boxShadow: '0 30px 90px rgba(0, 0, 0, 0.8)',
                      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      inset: '-2px',
                      background: 'linear-gradient(45deg, #C7B381, transparent, #C7B381)',
                      borderRadius: '16px',
                      opacity: hoveredCard === hero.id ? 0.6 : 0.3,
                      filter: 'blur(8px)',
                      transition: 'opacity 0.6s ease',
                      animation: 'rotate 8s linear infinite',
                      zIndex: 0
                    }}></div>

                    <div style={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(135deg, rgba(15, 20, 25, 0.98) 0%, rgba(26, 31, 46, 0.98) 100%)',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      zIndex: 1
                    }}>
                      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
                        <img
                          src={hero.image}
                          alt={hero.name}
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transform: hoveredCard === hero.id ? 'scale(1.08)' : 'scale(1)',
                            transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
                            filter: 'brightness(0.8)'
                          }}
                        />
                        <div style={{
                          position: 'absolute',
                          inset: 0,
                          background: isMobile
                            ? 'linear-gradient(180deg, transparent 0%, rgba(15, 20, 25, 0.95) 70%)'
                            : 'linear-gradient(90deg, rgba(15, 20, 25, 0.95) 0%, transparent 40%, transparent 100%)'
                        }}></div>
                      </div>

                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        padding: isMobile ? 'clamp(2rem, 5vw, 3rem)' : 'clamp(3rem, 6vw, 5rem)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        alignItems: isMobile ? 'center' : 'flex-start',
                        textAlign: isMobile ? 'center' : 'left'
                      }}>
                        <h3 style={{
                          fontSize: isMobile ? 'clamp(1.8rem, 7vw, 2.8rem)' : 'clamp(2.8rem, 5.5vw, 4.5rem)',
                          fontWeight: '300',
                          marginBottom: 'clamp(1rem, 2vw, 1.5rem)',
                          letterSpacing: '0.08em',
                          lineHeight: '1.15',
                          maxWidth: isMobile ? '100%' : '700px',
                          textTransform: 'uppercase'
                        }}>
                          <span style={{
                            fontSize: isMobile ? 'clamp(2.5rem, 9vw, 3.8rem)' : 'clamp(4rem, 7vw, 6rem)',
                            fontWeight: '700',
                            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6347 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            display: 'inline-block',
                            marginRight: '0.25em',
                            textShadow: '0 4px 20px rgba(255, 215, 0, 0.3)'
                          }}>
                            {hero.name.toUpperCase().split(' ')[0]}
                          </span>
                          <span style={{
                            background: 'linear-gradient(135deg, #E8D4A0 0%, #C7B381 50%, #A89968 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                          }}>
                            {hero.name.toUpperCase().split(' ').slice(1).join(' ')}
                          </span>
                        </h3>
                        <div style={{
                          fontSize: isMobile ? 'clamp(2rem, 6vw, 2.5rem)' : 'clamp(2.5rem, 5vw, 4rem)',
                          fontWeight: '300',
                          color: '#C7B381',
                          marginBottom: 'clamp(2rem, 4vw, 3rem)',
                          letterSpacing: '-0.02em'
                        }}>
                          {formatPrice(hero.price)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {others.length > 0 && (
                <div style={{
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 'clamp(2rem, 4vw, 3rem)',
                    padding: '0 clamp(0.5rem, 1vw, 1rem)'
                  }}>
                    <h3 style={{
                      fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                      fontWeight: '300',
                      color: '#FFFFFF',
                      letterSpacing: '-0.01em'
                    }}>
                      Also <span style={{ fontWeight: '600', color: '#C7B381' }}>Featured</span>
                    </h3>
                    <Link
                      to="/vehicles"
                      style={{
                        color: '#C7B381',
                        textDecoration: 'none',
                        fontSize: 'clamp(0.85rem, 1.7vw, 0.95rem)',
                        fontWeight: '600',
                        letterSpacing: '0.05em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.gap = '0.75rem';
                        e.currentTarget.style.color = '#E8D4A0';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.gap = '0.5rem';
                        e.currentTarget.style.color = '#C7B381';
                      }}
                    >
                      View All
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Link>
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: 'clamp(1.5rem, 3vw, 2rem)',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    padding: 'clamp(0.5rem, 1vw, 1rem) 0',
                    scrollSnapType: 'x mandatory',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#C7B381 transparent'
                  }}>
                    {others.map(vehicle => (
                      <div
                        key={vehicle.id}
                        onMouseEnter={() => setHoveredCard(vehicle.id)}
                        onMouseLeave={() => setHoveredCard(null)}
                        style={{
                          flex: '0 0 auto',
                          width: isMobile ? '280px' : 'clamp(320px, 28vw, 400px)',
                          scrollSnapAlign: 'start',
                          position: 'relative',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          transition: 'all 0.4s ease',
                          transform: hoveredCard === vehicle.id ? 'translateY(-12px)' : 'translateY(0)',
                          boxShadow: hoveredCard === vehicle.id
                            ? '0 24px 48px rgba(0, 0, 0, 0.6)'
                            : '0 8px 24px rgba(0, 0, 0, 0.4)'
                        }}
                      >
                        <div style={{
                          position: 'absolute',
                          inset: 0,
                          border: `1px solid ${hoveredCard === vehicle.id ? '#C7B381' : 'rgba(199, 179, 129, 0.2)'}`,
                          borderRadius: '12px',
                          pointerEvents: 'none',
                          transition: 'border-color 0.4s ease',
                          zIndex: 2
                        }}></div>

                        <div style={{
                          height: '100%',
                          background: 'rgba(15, 20, 25, 0.95)',
                          borderRadius: '12px',
                          overflow: 'hidden'
                        }}>
                          <div style={{ height: '240px', position: 'relative', overflow: 'hidden' }}>
                            <img
                              src={vehicle.image}
                              alt={vehicle.name}
                              loading="lazy"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.6s ease',
                                transform: hoveredCard === vehicle.id ? 'scale(1.1)' : 'scale(1)'
                              }}
                            />
                          </div>

                          <div style={{ padding: 'clamp(1.5rem, 3vw, 2rem)' }}>
                            <h4 style={{
                              fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                              fontWeight: '400',
                              marginBottom: '0.75rem',
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase',
                              lineHeight: '1.3'
                            }}>
                              <span style={{
                                fontSize: 'clamp(1.3rem, 2.5vw, 1.6rem)',
                                fontWeight: '700',
                                background: 'linear-gradient(135deg, #4169E1 0%, #1E90FF 50%, #00BFFF 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                display: 'inline-block',
                                marginRight: '0.2em'
                              }}>
                                {vehicle.name.toUpperCase().split(' ')[0]}
                              </span>
                              <span style={{
                                background: 'linear-gradient(135deg, #C7B381 0%, #E8D4A0 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                display: 'block',
                                marginTop: '0.25rem'
                              }}>
                                {vehicle.name.toUpperCase().split(' ').slice(1).join(' ')}
                              </span>
                            </h4>

                            <div style={{
                              fontSize: 'clamp(1.5rem, 3vw, 1.8rem)',
                              fontWeight: '300',
                              color: '#C7B381',
                              letterSpacing: '-0.01em'
                            }}>
                              {formatPrice(vehicle.price)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: 'clamp(6rem, 12vw, 10rem)',
              background: 'rgba(15, 20, 25, 0.6)',
              borderRadius: '16px',
              border: '1px solid rgba(199, 179, 129, 0.1)'
            }}>
              <div style={{ fontSize: 'clamp(4rem, 8vw, 6rem)', marginBottom: '2rem', opacity: 0.2 }} aria-hidden="true">
                ✨
              </div>
              <p style={{ fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)', color: '#808080', fontWeight: '300' }}>
                The spotlight awaits...
              </p>
            </div>
          )}
        </div>
<style>{`
  @keyframes pulse {
    0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
    50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.1); }
  }
  @keyframes shimmer {
    0% { background-position: 200% center; }
    100% { background-position: -200% center; }
  }
  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  /* New animations for phone screens */
  @keyframes float {
    0%, 100% { transform: translateY(0) scale(1.1); }
    50% { transform: translateY(-20px) scale(1.1); }
  }
  @keyframes floatLeft {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-15px); }
  }
  @keyframes floatRight {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-15px); }
  }
  @keyframes glow {
    0%, 100% { box-shadow: 0 0 20px rgba(199, 179, 129, 0.3), 0 0 40px rgba(199, 179, 129, 0.2); }
    50% { box-shadow: 0 0 40px rgba(199, 179, 129, 0.5), 0 0 80px rgba(199, 179, 129, 0.3); }
  }
  
  *::-webkit-scrollbar { height: 6px; }
  *::-webkit-scrollbar-track { background: rgba(199, 179, 129, 0.05); border-radius: 3px; }
  *::-webkit-scrollbar-thumb { background: rgba(199, 179, 129, 0.3); border-radius: 3px; }
  *::-webkit-scrollbar-thumb:hover { background: rgba(199, 179, 129, 0.5); }
`}</style>
      </section>

      {/* Services Section */}
      <section style={{
        padding: 'clamp(4rem, 10vw, 8rem) 0',
        background: 'linear-gradient(to bottom, #1A1F2E, #0F1419)',
        borderTop: '1px solid rgba(199, 179, 129, 0.1)',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        <div style={{ 
          maxWidth: '1400px', 
          margin: '0 auto', 
          padding: '0 clamp(2rem, 5vw, 4rem)',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(3.5rem, 7vw, 6rem)' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'clamp(0.5rem, 1.5vw, 0.75rem)',
              marginBottom: 'clamp(1rem, 2vw, 1.5rem)'
            }}>
              <div style={{ width: 'clamp(40px, 6vw, 60px)', height: '1px', background: 'linear-gradient(to right, transparent, #C7B381)' }}></div>
              <span style={{
                color: '#C7B381',
                fontSize: 'clamp(0.75rem, 1.6vw, 0.9rem)',
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
                fontWeight: '600'
              }}>
                Choose Your Journey
              </span>
              <div style={{ width: 'clamp(40px, 6vw, 60px)', height: '1px', background: 'linear-gradient(to left, transparent, #C7B381)' }}></div>
            </div>
            <h2 style={{
              fontSize: 'clamp(2.2rem, 6.5vw, 4rem)',
              fontWeight: '300',
              marginBottom: 'clamp(1rem, 2vw, 1.5rem)',
              color: '#FFFFFF',
              letterSpacing: '-0.01em'
            }}>
              Experience <span style={{ fontWeight: '600', color: '#C7B381' }}>Excellence</span>
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 'clamp(1.5rem, 3vw, 2.5rem)',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {[
              { to: '/vehicles', title: 'Cars In Our Showroom', desc: 'Discover our current premium electric vehicles available for immediate purchase' },
              { to: '/client-hiring', title: 'Hiring', desc: 'Experience premium EVs through our flexible and convenient hiring plans' },
              { to: '/lease-to-own-viewer', title: 'Lease to Own', desc: 'Smart plans that let you drive today and own your dream EV tomorrow' }
            ].map((service, idx) => (
              <Link key={idx} to={service.to} style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}>
                <div style={{
                  height: '100%',
                  padding: 'clamp(2.5rem, 4vw, 3.5rem)',
                  background: 'rgba(15, 20, 25, 0.7)',
                  border: '1px solid rgba(199, 179, 129, 0.2)',
                  borderRadius: '8px',
                  transition: 'all 0.35s ease',
                  cursor: 'pointer',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-12px)';
                    e.currentTarget.style.borderColor = '#C7B381';
                    e.currentTarget.style.boxShadow = '0 24px 48px rgba(199, 179, 129, 0.15)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'rgba(199, 179, 129, 0.2)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h3 style={{ fontSize: 'clamp(1.4rem, 3vw, 1.8rem)', fontWeight: '500', marginBottom: '1rem', color: '#FFFFFF' }}>
                    {service.title}
                  </h3>
                  <p style={{ fontSize: 'clamp(0.95rem, 2vw, 1.05rem)', color: '#B8B8B8', lineHeight: '1.6', maxWidth: '320px' }}>
                    {service.desc}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      {/* Mobile App Section */}
<section style={{
  padding: 'clamp(5rem, 10vw, 8rem) clamp(2rem, 5vw, 4rem)',
  background: 'linear-gradient(135deg, #1A1F2E 0%, #0F1419 100%)',
  position: 'relative',
  width: '100%',
  maxWidth: '100vw',
  boxSizing: 'border-box',
  overflow: 'hidden'
}}>
  <div style={{ 
    maxWidth: '1400px', 
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box'
  }}>
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: 'clamp(3rem, 6vw, 5rem)',
      alignItems: 'center'
    }}>
      <div style={{ order: isMobile ? 2 : 1 }}>
        <div style={{
          display: 'inline-block',
          padding: '0.5rem 1.25rem',
          background: 'rgba(199, 179, 129, 0.1)',
          border: '1px solid rgba(199, 179, 129, 0.3)',
          borderRadius: '30px',
          color: '#C7B381',
          fontSize: 'clamp(0.7rem, 1.4vw, 0.8rem)',
          fontWeight: '600',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          marginBottom: 'clamp(1.5rem, 3vw, 2rem)'
        }}>
          Download Our App
        </div>

        <h2 style={{
          fontSize: 'clamp(2.2rem, 5vw, 3.8rem)',
          fontWeight: '700',
          color: '#FFFFFF',
          marginBottom: 'clamp(1rem, 2vw, 1.5rem)',
          letterSpacing: '-0.02em',
          lineHeight: '1.1'
        }}>
          Our ST Motors,
          <br />
          <span style={{ color: '#C7B381' }}>In Your Pocket</span>
        </h2>

        <p style={{
          fontSize: 'clamp(1rem, 1.8vw, 1.1rem)',
          color: '#B8B8B8',
          lineHeight: '1.7',
          marginBottom: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: '300'
        }}>
          Send booking requests, explore our collection, manage your fleet, and stay updated with exclusive offers—all from your mobile device.
        </p>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'clamp(0.75rem, 1.5vw, 1rem)',
          marginBottom: 'clamp(2rem, 4vw, 3rem)'
        }}>
          {[
            'Instant booking requests',
            'Browse complete EV catalog',
            'Track your rentals & leases',
            'Keep favourite collections of our cars'
          ].map((feature, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 'clamp(0.75rem, 1.5vw, 1rem)' }}>
              <div style={{
                width: 'clamp(24px, 4vw, 28px)',
                height: 'clamp(24px, 4vw, 28px)',
                borderRadius: '50%',
                background: 'rgba(199, 179, 129, 0.15)',
                border: '1px solid rgba(199, 179, 129, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M2 7L6 11L12 3" stroke="#C7B381" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <span style={{
                fontSize: 'clamp(0.95rem, 1.7vw, 1.05rem)',
                color: '#E8E8E8',
                fontWeight: '400'
              }}>
                {feature}
              </span>
            </div>
          ))}
        </div>
<div style={{ display: 'flex', gap: 'clamp(1rem, 2vw, 1.5rem)', flexWrap: 'wrap' }}>
  <a
            href="https://apps.apple.com/app/st-motors-ev/id6754755756"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download on App Store"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: 'clamp(0.9rem, 1.8vw, 1.1rem) clamp(1.5rem, 3vw, 2rem)',
              background: 'linear-gradient(135deg, #C7B381 0%, #A89968 100%)',
              color: '#0F1419',
              borderRadius: '12px',
              textDecoration: 'none',
              fontSize: 'clamp(0.85rem, 1.5vw, 0.95rem)',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: '0 8px 24px rgba(199, 179, 129, 0.3)'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(199, 179, 129, 0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(199, 179, 129, 0.3)';
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            <div>
              <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>Download on</div>
              <div style={{ fontWeight: '700' }}>App Store</div>
            </div>
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.company.showroom"  
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get it on Google Play"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: 'clamp(0.9rem, 1.8vw, 1.1rem) clamp(1.5rem, 3vw, 2rem)',
              background: 'rgba(199, 179, 129, 0.1)',
              color: '#C7B381',
              border: '2px solid rgba(199, 179, 129, 0.3)',
              borderRadius: '12px',
              textDecoration: 'none',
              fontSize: 'clamp(0.85rem, 1.5vw, 0.95rem)',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(199, 179, 129, 0.2)';
              e.currentTarget.style.borderColor = 'rgba(199, 179, 129, 0.5)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(199, 179, 129, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(199, 179, 129, 0.3)';
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
            </svg>
            <div>
              <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>GET IT ON</div>
              <div style={{ fontWeight: '700' }}>Google Play</div>
            </div>
          </a>
        </div>
      </div>

      {/* Screenshots */}
      <div style={{
        order: isMobile ? 1 : 2,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        minHeight: isMobile ? '400px' : '500px'
      }}>
        {/* Center Phone - Main */}
        <div style={{
          position: 'relative',
          zIndex: 2,
          transform: 'scale(1.1)',
          filter: 'drop-shadow(0 30px 60px rgba(0, 0, 0, 0.5))',
          animation: 'float 6s ease-in-out infinite'
        }}>
          <div style={{
            width: isMobile ? '220px' : '280px',
            height: isMobile ? '450px' : '580px',
            borderRadius: '36px',
            border: '8px solid #1A1F2E',
            overflow: 'hidden',
            boxShadow: '0 0 0 2px rgba(199, 179, 129, 0.3)',
            background: '#000',
            animation: 'glow 3s ease-in-out infinite'
          }}>
            <img
              src={require('./assets/screenshots/1.jpeg')}
              alt="ST Motors App Screenshot - Main View"
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* Left Phone */}
        <div style={{
          position: 'absolute',
          left: isMobile ? '-10%' : '-15%',
          top: '10%',
          opacity: 0.4,
          filter: 'blur(2px)',
          zIndex: 1,
          animation: 'floatLeft 5s ease-in-out infinite'
        }}>
          <div style={{
            width: isMobile ? '180px' : '240px',
            height: isMobile ? '370px' : '490px',
            borderRadius: '30px',
            border: '6px solid #1A1F2E',
            overflow: 'hidden',
            background: '#000'
          }}>
            <img
              src={require('./assets/screenshots/2.jpeg')}
              alt="ST Motors App Screenshot - Feature View"
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* Right Phone */}
        <div style={{
          position: 'absolute',
          right: isMobile ? '-10%' : '-15%',
          bottom: '10%',
          opacity: 0.4,
          filter: 'blur(2px)',
          zIndex: 1,
          animation: 'floatRight 5.5s ease-in-out infinite'
        }}>
          <div style={{
            width: isMobile ? '180px' : '240px',
            height: isMobile ? '370px' : '490px',
            borderRadius: '30px',
            border: '6px solid #1A1F2E',
            overflow: 'hidden',
            background: '#000'
          }}>
            <img
              src={require('./assets/screenshots/3.jpeg')}
              alt="ST Motors App Screenshot - Detail View"
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* Glow Effect Background */}
        <div style={{
          position: 'absolute',
          inset: '-20%',
          background: 'radial-gradient(circle, rgba(199, 179, 129, 0.15) 0%, transparent 70%)',
          filter: 'blur(40px)',
          zIndex: 0,
          pointerEvents: 'none'
        }}></div>
      </div>
    </div>
  </div>
</section>
      {/* Footer */}
      <footer style={{
        background: '#0F1419',
        borderTop: '1px solid rgba(199, 179, 129, 0.15)',
        padding: 'clamp(3rem, 6vw, 5rem) 0 clamp(1.5rem, 3vw, 2rem)',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        <div style={{ 
          maxWidth: '1400px', 
          margin: '0 auto', 
          padding: '0 clamp(2rem, 5vw, 4rem)',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 'clamp(2.5rem, 5vw, 4rem)',
            marginBottom: 'clamp(3rem, 5vw, 4rem)'
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(0.5rem, 1.5vw, 0.75rem)', marginBottom: 'clamp(1rem, 2vw, 1.5rem)' }}>
                <img
                  src={logo}
                  alt="ST Motors Logo"
                  style={{
                    height: 'clamp(45px, 7vw, 55px)',
                    width: 'auto',
                    objectFit: 'contain'
                  }}
                />
                <div>
                  <div style={{ fontSize: 'clamp(1.1rem, 2vw, 1.25rem)', fontWeight: '600', color: '#FFFFFF' }}>
                    ST MOTORS
                  </div>
                </div>
              </div>
              <p style={{
                fontSize: 'clamp(0.85rem, 1.7vw, 0.9rem)',
                color: '#808080',
                lineHeight: '1.7',
                fontWeight: '300'
              }}>
                Discover the finest selection of premium electric vehicles with ST Motors.
              </p>
            </div>

            <div>
              <h4 style={{
                fontSize: 'clamp(0.85rem, 1.7vw, 0.9rem)',
                fontWeight: '600',
                color: '#C7B381',
                marginBottom: 'clamp(1rem, 2vw, 1.5rem)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase'
              }}>
                Quick Links
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(0.6rem, 1.3vw, 0.75rem)' }}>
                {[
                  { label: 'Our Fleet', to: '/vehicles' },
                  { label: 'Find Us', to: '/locations' },
                  { label: 'About Us', to: '/company-background' },
                ].map((link, idx) => (
                  <Link
                    key={idx}
                    to={link.to}
                    style={{
                      color: '#B8B8B8',
                      textDecoration: 'none',
                      fontSize: 'clamp(0.85rem, 1.7vw, 0.9rem)',
                      transition: 'color 0.3s ease',
                      fontWeight: '300'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#C7B381'}
                    onMouseLeave={(e) => e.target.style.color = '#B8B8B8'}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h4 style={{
                fontSize: 'clamp(0.85rem, 1.7vw, 0.9rem)',
                fontWeight: '600',
                color: '#C7B381',
                marginBottom: 'clamp(1rem, 2vw, 1.5rem)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase'
              }}>
                Contact
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(0.8rem, 1.7vw, 1rem)' }}>
                <div style={{ fontSize: 'clamp(0.85rem, 1.7vw, 0.9rem)', color: '#B8B8B8', fontWeight: '300' }}>
                  <div style={{ marginBottom: '0.25rem', color: '#808080', fontSize: 'clamp(0.7rem, 1.4vw, 0.75rem)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Phone
                  </div>
                  <a href="tel:+97577248888" style={{ color: 'inherit', textDecoration: 'none' }}>+975 77248888</a>
                </div>
                <div style={{ fontSize: 'clamp(0.85rem, 1.7vw, 0.9rem)', color: '#B8B8B8', fontWeight: '300' }}>
                  <div style={{ marginBottom: '0.25rem', color: '#808080', fontSize: 'clamp(0.7rem, 1.4vw, 0.75rem)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Email
                  </div>
                  <a href="mailto:marketting@sangaytsheltrim.com" style={{ color: 'inherit', textDecoration: 'none' }}>marketting@sangaytsheltrim.com</a>
                </div>
                <div style={{ fontSize: 'clamp(0.85rem, 1.7vw, 0.9rem)', color: '#B8B8B8', fontWeight: '300' }}>
                  <div style={{ marginBottom: '0.25rem', color: '#808080', fontSize: 'clamp(0.7rem, 1.4vw, 0.75rem)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Hours
                  </div>
                  24/7 Service
                </div>
              </div>
            </div>

            <div>
              <h4 style={{
                fontSize: 'clamp(0.85rem, 1.7vw, 0.9rem)',
                fontWeight: '600',
                color: '#C7B381',
                marginBottom: 'clamp(1rem, 2vw, 1.5rem)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase'
              }}>
                Connect With Us
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(0.8rem, 1.7vw, 1rem)' }}>
                {[
                  {
                    name: 'Facebook',
                    url: 'https://www.facebook.com/share/171iRhNYbV/',
                    icon: (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    )
                  },
                  {
                    name: 'Instagram',
                    url: 'https://www.instagram.com/sangaytsheltrimmotors?igsh=MTJ6OHdwbjAybWF5YQ==',
                    icon: (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    )
                  },
                  {
                    name: 'TikTok',
                    url: 'https://vt.tiktok.com/ZS5GS5oTY/',
                    icon: (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                      </svg>
                    )
                  }
                ].map((social, idx) => (
                  <a
                    key={idx}
                    href={handleExternalLink(social.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Follow us on ${social.name}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'clamp(0.5rem, 1vw, 0.75rem)',
                      color: '#B8B8B8',
                      textDecoration: 'none',
                      fontSize: 'clamp(0.85rem, 1.7vw, 0.9rem)',
                      transition: 'all 0.3s ease',
                      fontWeight: '300'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#C7B381';
                      e.currentTarget.style.transform = 'translateX(5px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#B8B8B8';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <div style={{
                      width: 'clamp(36px, 6vw, 42px)',
                      height: 'clamp(36px, 6vw, 42px)',
                      borderRadius: '50%',
                      background: 'rgba(199, 179, 129, 0.1)',
                      border: '1px solid rgba(199, 179, 129, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      flexShrink: 0
                    }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(199, 179, 129, 0.2)';
                        e.currentTarget.style.borderColor = 'rgba(199, 179, 129, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(199, 179, 129, 0.1)';
                        e.currentTarget.style.borderColor = 'rgba(199, 179, 129, 0.2)';
                      }}
                    >
                      {social.icon}
                    </div>
                    <span>{social.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div style={{
            paddingTop: 'clamp(1.5rem, 3vw, 2rem)',
            borderTop: '1px solid rgba(199, 179, 129, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 'clamp(1rem, 2vw, 1.5rem)'
          }}>
            <div style={{ fontSize: 'clamp(0.8rem, 1.6vw, 0.85rem)', color: '#808080', fontWeight: '300' }}>
              © 2026 Sangay Tsheltrim Pvt Limited. All rights reserved. | Developed by{' '}
              <a
                href={handleExternalLink("https://www.linkedin.com/in/arjun-mongar")}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#C7B381',
                  textDecoration: 'none',
                  fontWeight: '400',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#E8D4A0'}
                onMouseLeave={(e) => e.target.style.color = '#C7B381'}
              >
                Arjun Mongar
              </a>
            </div>
            <div style={{ display: 'flex', gap: 'clamp(1.5rem, 3vw, 2rem)', flexWrap: 'wrap' }}>
              {[
                { label: 'Privacy Policy', to: '/privacy-policy' },
                { label: 'Terms of Service', to: '/terms-of-service' }
              ].map((link, idx) => (
                <Link
                  key={idx}
                  to={link.to}
                  style={{
                    color: '#808080',
                    textDecoration: 'underline',
                    fontSize: 'clamp(0.8rem, 1.6vw, 0.85rem)',
                    transition: 'color 0.3s ease',
                    fontWeight: '300'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#C7B381'}
                  onMouseLeave={(e) => e.target.style.color = '#808080'}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomeScreen;